import './styles.scss';

import { Accordion, Button, Container, InputGroup, Row } from 'react-bootstrap';
import { ChatQuickReplyMessage, Trip } from '@/models/gen/graphql';
import { ChatRoomType, useChatState } from '@/hooks/useSocket/chat/useChatRooms';
import {
  FormEvent,
  HTMLAttributes,
  MutableRefObject,
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Validation, bytesToSize, getBase64String, getClasses, queryInput, scrollTextAreaToReveal, stringify } from '@/utils';
import { fillQuickReplyTripTokens, getTripTokens } from './quickReplyTokens';

import ChatMessage from '../../ChatMessage';
import ChatStatus from '../../ChatStatus';
import Field from '@/components/Field';
import HasPermission from '@/components/HasPermission';
import Textarea from '@/common/Textarea';
import Tippy from '@tippyjs/react';
import { WheelEvent } from 'react';
import { getUserId } from '@/hooks/useSocket/utils';
import { refetchSearchTrips } from '@/api/services/trips/searchTrips';
import { searchQuickReplyTemplates } from '@/features/Trips/api/services/searchQuickReplyTemplates';
import useChatMethods from '@/hooks/useSocket/chat/useChatMethods';
import useInterval from '@/hooks/useInterval';
import useSocket from '@/hooks/useSocket';
import useVisibility from '@/hooks/useVisibility';

type ChatListContentProps = HTMLAttributes<HTMLDivElement> & {
  roomId?: string;
  roomType?: ChatRoomType;
  height: number;
};

const ChatListContent = ({ roomId, roomType, height, ...props }: ChatListContentProps) => {
  const socket = useSocket();
  const [history, config, setChatState] = useChatState(({ state: { history, config }, setState }) => [history, config, setState]);
  const setHistory = useCallback((history: any) => setChatState((state) => ({ ...state, history })), [setChatState]);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const { getHistory } = useChatMethods();
  const isVisible = useVisibility();
  const lastHistory = useRef<any>(null);
  const retries = useRef(0);

  const scrollToBottom = () => messageEndRef.current?.scrollTo?.(0, messageEndRef.current.scrollHeight);

  const getChatHistory = async (retry: boolean = true) =>
    getHistory({
      rooms: [roomId],
      limit: 100,
      offset: 0,
    }).catch(() => {
      console.error(`Failed to get history.`);
    });

  useEffect(() => {
    setChatState((current) => ({ ...current, roomId }));
    setHistory([]);
    getChatHistory();
  }, [roomId]);

  useEffect(() => {
    if (stringify.compare(lastHistory.current, history)) return;
    lastHistory.current = history;
    scrollToBottom();
  }, [scrollToBottom, history, roomId]);

  useInterval(() => {
    if (!isVisible || !roomId || !socket?.connected) return;
    getChatHistory(false);
  }, config?.client_refresh_interval_milliseconds ?? 5000);

  return (
    <div {...props} className={getClasses('ChatList-Content', props?.className)}>
      <Accordion.Body className="py-0" onEntered={() => scrollToBottom()}>
        <Container fluid className="d-flex flex-column p-0 {max-height:100%;}">
          <Row className="flex-grow-1">
            <ChatListContent.Messages messages={history || []} ref={messageEndRef} height={height} />
          </Row>
          <Row>
            <ChatListContent.Footer roomId={roomId} roomType={roomType} />
          </Row>
        </Container>
      </Accordion.Body>
    </div>
  );
};

type ChatListContentMessagesProps = HTMLAttributes<HTMLDivElement> & {
  messages: any;
  height: number;
};
const ChatListContentMessages = ({ messages, height, ...props }: ChatListContentMessagesProps, ref: MutableRefObject<HTMLDivElement>) => {
  return (
    <div {...props} className={getClasses('ChatList-Messages', props?.className)} style={{ height: height * 0.5 }} ref={ref}>
      <div className="d-flex flex-column-reverse">
        {messages?.map?.((message, index) => {
          return <ChatMessage message={message} key={index} />;
        })}
      </div>
    </div>
  );
};

type ChatListContentFooterProps = HTMLAttributes<HTMLDivElement> & {
  roomId?: string;
  roomType?: ChatRoomType;
};
const ChatListContentFooter = ({ roomId, roomType, ...props }: ChatListContentFooterProps) => {
  return (
    <div {...props} className={getClasses('ChatList-Footer', props?.className)}>
      <div className="d-flex flex-column">
        <ChatStatus roomId={roomId} />
        <HasPermission name="allowSendChat">
          <div className="d-flex gap-2">
            <ChatListContent.Input roomId={roomId} roomType={roomType} />
          </div>
        </HasPermission>
      </div>
    </div>
  );
};

// Matches bracketed placeholders (e.g. "<x>", "<#>"), but not < x >.
const QUICK_REPLY_PLACEHOLDER_REGEX = /<[^> ]*>/;

const findQuickReplyPlaceholder = (value: string, fromIndex: number): [number, number] => {
  const match = QUICK_REPLY_PLACEHOLDER_REGEX.exec(value.slice(fromIndex));
  if (!match) return [value.length, value.length];
  const from = fromIndex + match.index;
  const to = from + match[0].length;
  return [from, to];
};

type ChatListContentChatInputProps = HTMLAttributes<HTMLDivElement> & {
  roomId?: string;
  roomType?: ChatRoomType;
};

const scrollYOnWheel = (event: WheelEvent<HTMLElement>): void => {
  if (event.deltaY === 0) return;
  event.currentTarget.scrollLeft += event.deltaY;
};

const ChatListContentChatInput = ({ roomId, roomType, ...props }: ChatListContentChatInputProps) => {
  const [rooms, setChatState] = useChatState(({ state: { rooms }, setState }) => [rooms[roomType], setState]);
  const { sendMessage, getUsersTyping } = useChatMethods();
  const [files, setFiles] = useState<File[]>([]);
  const userId = getUserId();

  const handleSendImage = useCallback(
    async (files) => {
      for (const file of files) {
        const type = file.type;
        const content = await getBase64String(file);
        sendMessage({
          ...(rooms?.[roomId] && {
            trip_id: rooms[roomId]?.trip?.tripId || null,
            trip_scheduled: rooms[roomId]?.trip?.tripScheduled || null,
            flight_number: rooms[roomId]?.trip?.tripFlightNumber || null,
            servicer_iata_airline_code: rooms[roomId]?.trip?.servicerIataAirlineCode || null,
            room_display_name: rooms[roomId]?.displayName || null,
            room_display_image_url: rooms[roomId]?.displayImageUrl || null,
            loop_name: rooms[roomId]?.latestMessage?.loop_name || null,
          }),
          room: roomId,
          content: content,
          content_type: type,
        });
      }
      setFiles([]);
      filesRef.current.value = '';
    },
    [sendMessage, rooms, roomId]
  );
  const deleteImage = (index: number) => {
    if (files) setFiles(files.filter((_, i) => i !== index));
    filesRef.current.value = '';
  };
  const filesRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [message, setMessage] = useState('');
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${input.scrollHeight}px`;
  }, [message]);
  const [quickReplyTemplates, setQuickReplyTemplates] = useState<ChatQuickReplyMessage[]>([]);
  useEffect(() => {
    searchQuickReplyTemplates().then(setQuickReplyTemplates);
  }, []);
  const tripId = rooms?.[roomId]?.trip?.tripId;
  const [tripLoading, setTripLoading] = useState(false);
  const [quickReplySelection, setQuickReplySelection] = useState(0);
  const pendingSelectionRangeRef = useRef<[number, number] | null>(null);
  const applyQuickReply = (template: ChatQuickReplyMessage, trip: Trip | null) => {
    const tokens = getTripTokens(trip);
    const message = fillQuickReplyTripTokens(template.message, tokens);
    pendingSelectionRangeRef.current = findQuickReplyPlaceholder(message, 0);
    setMessage(message);
    setQuickReplySelection((selection) => selection + 1);
  };
  const selectQuickReply = async (template: ChatQuickReplyMessage) => {
    if (tripLoading) return;
    if (!Validation.isValidUUID(tripId)) return applyQuickReply(template, null);
    setTripLoading(true);
    try {
      const {
        rows: [trip],
      } = await refetchSearchTrips([{ id: queryInput(tripId) }]);
      applyQuickReply(template, trip ?? null);
    } catch (err) {
      console.error(err?.message || err);
    } finally {
      setTripLoading(false);
    }
  };
  useLayoutEffect(() => {
    if (!pendingSelectionRangeRef.current) return;
    const [start, end] = pendingSelectionRangeRef.current;
    pendingSelectionRangeRef.current = null;
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.setSelectionRange(start, end);
    scrollTextAreaToReveal(input, start);
  }, [quickReplySelection]);
  const hasUnfilledPlaceholder = useMemo(() => QUICK_REPLY_PLACEHOLDER_REGEX.test(message), [message]);
  const submitMessage = useCallback(async () => {
    // TODO: check that no files are being uploaded
    if (files?.length > 0) await handleSendImage(files);
    if (!message || message.replace(/ {1,}/g, '').length < 3) return;
    if (QUICK_REPLY_PLACEHOLDER_REGEX.test(message)) return;
    const payload = {
      ...(rooms?.[roomId] && {
        trip_id: rooms[roomId]?.trip?.tripId || null,
        trip_scheduled: rooms[roomId]?.trip?.tripScheduled || null,
        trip_flight_number: rooms[roomId]?.trip?.tripFlightNumber || null,
        servicer_iata_airline_code: rooms[roomId]?.trip?.servicerIataAirlineCode || null,
        room_display_name: rooms[roomId]?.displayName || null,
        room_display_image_url: rooms[roomId]?.displayImageUrl || null,
        loop_name: rooms[roomId]?.latestMessage?.loop_name || null,
      }),
      room: roomId ? roomId : '',
      content: message,
      content_type: 'text/plain',
    };
    sendMessage(payload);
    setMessage('');
    setChatState((current) => ({
      ...current,
      usersTyping: current.usersTyping.filter((user) => user.room_id !== roomId && user.user_id !== userId),
    }));
  }, [files, handleSendImage, message, rooms, roomId, sendMessage, userId, setChatState]);
  // If a placeholder is still unfilled, Enter jumps to it instead of sending; only sends once all are filled.
  const handleEnterKey = useCallback(() => {
    const input = inputRef.current;
    if (input && hasUnfilledPlaceholder) {
      let [start, end] = findQuickReplyPlaceholder(message, input.selectionEnd || 0);
      if (start === end) [start, end] = findQuickReplyPlaceholder(message, 0);
      if (start !== end) {
        input.setSelectionRange(start, end);
        scrollTextAreaToReveal(input, start);
        return;
      }
    }
    submitMessage();
  }, [message, hasUnfilledPlaceholder, submitMessage]);

  const messageLength = useMemo(() => message?.replace?.(/ {1,}/g, '')?.length, [message]);
  const isValid = useMemo(
    () =>
      !hasUnfilledPlaceholder &&
      ((files?.length > 0 && (messageLength === 0 || messageLength >= 3)) || (files?.length === 0 && messageLength >= 3)),
    [files, messageLength, hasUnfilledPlaceholder]
  );
  const isInvalid = useMemo(
    () =>
      (hasUnfilledPlaceholder && messageLength > 0) ||
      (files.length === 0 && messageLength > 0 && messageLength < 3) ||
      (messageLength > 0 && messageLength < 3),
    [files, messageLength, hasUnfilledPlaceholder]
  );
  const feedback = useMemo(() => {
    if (hasUnfilledPlaceholder && messageLength > 0) return 'Replace the highlighted placeholder before sending';
    return (files?.length > 0 && (messageLength === 0 || messageLength >= 3)) ||
      (files?.length === 0 && (messageLength === 0 || messageLength >= 3))
      ? undefined
      : 'Must be at least 3 characters or an image';
  }, [files, messageLength, hasUnfilledPlaceholder]);
  const validity = useMemo(
    () => (isValid === true && isInvalid === false ? true : isInvalid === true && isValid === false ? false : undefined),
    [isValid, isInvalid]
  );

  return (
    <div className="d-flex flex-column gap-2 w-100">
      {quickReplyTemplates.length > 0 && (
        <div className="ChatList-QuickReplies d-flex align-items-center gap-2 overflow-x-auto" onWheel={scrollYOnWheel}>
          {quickReplyTemplates.map((template) => (
            <Tippy key={template?.id} content={template?.message}>
              <Button
                variant="light"
                size="sm"
                id={template?.id}
                className="ChatList-QuickReply rounded-pill text-nowrap"
                onClick={(e) => {
                  e.preventDefault();
                  selectQuickReply(template);
                }}
                disabled={tripLoading}
              >
                {template?.name}
              </Button>
            </Tippy>
          ))}
          {tripLoading && <i className="fa fa-spinner fa-pulse" />}
        </div>
      )}
      <div className="d-flex gap-2">
        <div className="ChatList-ImageUploadContainer">
          <Button
            as="div"
            onClick={() => filesRef.current?.click()}
            className={getClasses('ChatList-ImageUpload', props?.className)}
            disabled={tripLoading}
          >
            <i className="sv sv-picture"></i>
          </Button>
          <input
            type="file"
            ref={filesRef}
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e: FormEvent<HTMLInputElement>) => {
              const { files } = e.target as HTMLInputElement;
              setFiles(Array.from(files));
            }}
          />
        </div>
        <div {...props} className={getClasses('ChatList-ChatInput', props?.className)}>
          {files?.map?.((file, index) => {
            return (
              <ChatListContent.Image
                onDelete={deleteImage}
                display={`${file.name} (${bytesToSize(file.size)})`}
                index={index}
                key={index}
              />
            );
          })}
          <InputGroup>
            <Field valid={validity} feedback={feedback}>
              <Textarea
                ref={inputRef}
                rows={1}
                autoSelect={false}
                disabled={tripLoading}
                onEnter={(_value, event) => {
                  event.stopPropagation();
                  if (event.shiftKey) return;
                  event.preventDefault();
                  handleEnterKey();
                }}
                onChange={(value) => {
                  getUsersTyping({
                    room_id: roomId,
                    user_id: userId,
                  });
                  setMessage((value as string) ?? '');
                }}
                value={message}
                isValid={isValid}
                isInvalid={isInvalid}
                placeholder={tripLoading ? 'Loading trip details…' : 'Type a message'}
              />
            </Field>
            <Button
              variant="icon"
              onClick={submitMessage}
              className={getClasses('ChatList-ChatSubmit', props?.className)}
              disabled={isInvalid || tripLoading}
            >
              <i className="sv sv-paper-plane" />
            </Button>
          </InputGroup>
        </div>
      </div>
    </div>
  );
};

type ChatListContentImageProps = HTMLAttributes<HTMLDivElement> & {
  display?: string;
  index?: number;
  onDelete?: (index: number) => void;
};
const ChatListContentImage = ({ display, index, onDelete, ...props }: ChatListContentImageProps) => {
  return (
    <InputGroup {...props} className={getClasses('ChatList-Image', props?.className)}>
      <InputGroup.Text className="ChatList-ImageName">{display}</InputGroup.Text>
      <Button onClick={() => onDelete(index)} className="ChatList-ImageDelete">
        <i className="sv sv-trash"></i>
      </Button>
    </InputGroup>
  );
};

type ChatListContentImageNameProps = HTMLAttributes<HTMLButtonElement> & {};
const ChatListContentImageName = ({ ...props }: ChatListContentImageNameProps) => {
  return (
    <InputGroup.Text {...props} className={getClasses('ChatList-ImageName', props?.className)}>
      Image Name
    </InputGroup.Text>
  );
};

type ChatListContentImageDeleteProps = HTMLAttributes<HTMLButtonElement> & {};
const ChatListContentImageDelete = ({ ...props }: ChatListContentImageDeleteProps) => {
  return (
    <Button {...props} onClick={() => alert('Deleting image')} className={getClasses('ChatList-ImageDelete', props?.className)}>
      <i className="sv sv-trash"></i>
    </Button>
  );
};

// Image
ChatListContent.Image = ChatListContentImage;
ChatListContent.ImageName = ChatListContentImageName;
ChatListContent.ImageDelete = ChatListContentImageDelete;

// Input
ChatListContent.Input = ChatListContentChatInput;

// Layout
ChatListContent.Footer = ChatListContentFooter;
ChatListContent.Messages = forwardRef(ChatListContentMessages);
export default ChatListContent;
